import React, { useState } from 'react';
import styled from 'styled-components';

import AlertHelp from '@rover/icons/svg/alert-help.svg';
import TrustLogo from '@rover/icons/svg/trust-logo.svg';
import { Box, Button, Flex, Heading, Paragraph, Text } from '@rover/kibble/core';
import Modal from '@rover/kibble/official-patterns/Modal';
import MQ from '@rover/kibble/styles/MediaQuery';

// TODO (DEV-104195): Translate text

type RoverProtectModalContentProps = {
  closeModal: () => void;
};

const RoverProtectModalContent = ({ closeModal }: RoverProtectModalContentProps): JSX.Element => {
  return (
    <Box>
      <TrustLogo width={36} height={48} />
      <Heading size="300" mt="4x" fontWeight="semibold">
        What is RoverProtect?
      </Heading>
      <Paragraph>
        <strong>
          Every training session is backed by tools to help keep you and your pet safe. You’ll have:
        </strong>
      </Paragraph>
      <Paragraph>
        <strong>Screened trainers</strong> that have already passed a third-party background check
        and show verified reviews from other pet parents, like you.
      </Paragraph>
      <Paragraph>
        <strong>Messaging & photo communication</strong> from your trainer before and after
        sessions.
      </Paragraph>
      <Paragraph>
        <strong>24/7 support</strong> from the Rover team–here to help if you ever need someone to
        talk to.
      </Paragraph>
      <Flex flexDirection="column" mt={['6x', '12x']}>
        <Button
          mb="4x"
          variant="primary"
          onClick={closeModal}
          fullWidth
          px={['4x', '6x']}
          py={['2x', '3x']}
        >
          Got it
        </Button>
        <Button
          href="/rover-protect"
          webProps={{ hrefTarget: '_blank' }}
          variant="flat"
          mb={['0x', '4x']}
          fullWidth
          px={['4x', '6x']}
          py={['2x', '3x']}
        >
          Learn more about RoverProtect
        </Button>
      </Flex>
    </Box>
  );
};

const RoverProtectBanner = (): JSX.Element => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const closeModal = (): void => {
    setModalOpen(false);
  };

  return (
    <Flex
      background="accent_secondary"
      borderRadius="secondary"
      flexDirection="column"
      gap="3x"
      mb={['0x', '2x']}
      padding="4x"
    >
      <Flex flexDirection="row" sx={{ gap: '10px' }} alignItems="center">
        <Flex minWidth="fit-content">
          <TrustLogo height="24" width="18" />
        </Flex>
        <Text size="100" sx={{ lineHeight: '1.5 !important' }}>
          All trainers are certified and pass a background check.{' '}
          <Text
            size="100"
            textDecoration="underline"
            fontWeight="semibold"
            onPress={() => setModalOpen(true)}
            sx={{ cursor: 'pointer' }}
            data-testid="background-check-learn-more"
          >
            Learn more
          </Text>
        </Text>
      </Flex>
      <Modal isOpen={modalOpen} onRequestClose={closeModal}>
        <RoverProtectModalContent closeModal={closeModal} />
      </Modal>
    </Flex>
  );
};

// eslint-disable-next-line rover/prefer-kibble-components
const ReadMoreButton = styled.button`
  position: absolute;
  right: 0;
  cursor: pointer;
  background: none;
  border: 0 none;
  margin: 0;
  padding: 0;
`;

// eslint-disable-next-line rover/prefer-kibble-components
const ReadMoreText = styled.span`
  text-decoration: underline;
  font-weight: 700;
  padding-left: 3ch;
  background: linear-gradient(
    to right,
    rgba(18, 18, 18, 0),
    ${({ theme }) => theme.colors.blue['100']} 2ch
  );
  color: ${({ theme }) => theme.colors.blue['600']};
  &:hover {
    color: ${({ theme }) => theme.colors.blue['700']};
  }
`;

interface FaqPreviewBannerProps {
  onReadMore: () => void;
}

const FaqPreviewBanner = ({ onReadMore }: FaqPreviewBannerProps): JSX.Element => {
  return (
    <Flex background="accent_secondary" borderRadius="secondary" gap="2x" padding="4x">
      <Flex>
        <AlertHelp height="20" width="20" />
      </Flex>
      <Flex flexDirection="column" gap="1x">
        <Text size="100" fontWeight="semibold" sx={{ lineHeight: '1.5 !important' }}>
          How do trainers get approved to list on Rover?
        </Text>
        <Text
          size="100"
          sx={{
            position: 'relative',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          Credentials and rewards-based training is requested{' '}
          <ReadMoreButton type="button" onClick={onReadMore}>
            <ReadMoreText>Read more</ReadMoreText>
          </ReadMoreButton>
        </Text>
      </Flex>
    </Flex>
  );
};

// eslint-disable-next-line rover/prefer-kibble-components
export const TabletAndDownBox = styled(Box)`
  display: none;

  ${MQ.TABLET_DOWN.toString()} {
    display: initial;
  }
`;

// eslint-disable-next-line rover/prefer-kibble-components
export const DesktopBox = styled(Box)`
  display: initial;

  ${MQ.TABLET_DOWN.toString()} {
    display: none;
  }
`;

export interface ResultsInfoBannerProps {
  onFaqPreviewBannerReadMore: () => void;
}

const ResultsInfoBanner = ({ onFaqPreviewBannerReadMore }: ResultsInfoBannerProps): JSX.Element => {
  return (
    <>
      <TabletAndDownBox>
        <FaqPreviewBanner onReadMore={onFaqPreviewBannerReadMore} />
      </TabletAndDownBox>
      <DesktopBox>
        <RoverProtectBanner />
      </DesktopBox>
    </>
  );
};

export default ResultsInfoBanner;
